import { useCallback, useEffect, useState } from "react";
import { Lock, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { AccessReportButton } from "@/components/meridian/AccessReportButton";

const inp = "text-xs border border-slate-300 rounded px-2 py-1.5 bg-white outline-none focus:border-sky-500";

function PermChecks({ permissions, value, onChange, id, disabled }) {
  return (
    <div className="flex flex-wrap gap-1" data-testid={`perm-checks-${id}`}>
      {Object.entries(permissions).map(([k, label]) => {
        const on = value.includes(k);
        return (
          <button key={k} type="button" title={label} disabled={disabled} data-testid={`perm-${id}-${k}`} onClick={() => onChange(on ? value.filter((x) => x !== k) : [...value, k])}
            className={`text-[10px] num px-1.5 py-0.5 rounded border transition-colors disabled:opacity-50 ${on ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-800" : "border-slate-300 text-slate-400 hover:text-slate-700"}`}>{k}</button>
        );
      })}
    </div>
  );
}

function AddUser({ roles, permissions, onSaved }) {
  const [f, setF] = useState({ name: "", role_key: "credit_analyst", permissions: roles.credit_analyst.permissions });
  const save = async () => {
    try { const { data } = await api.post("/admin/users", f); toast.success(`${data.name} added as ${data.role}`); setF({ ...f, name: "" }); onSaved(); }
    catch (e) { if (e.response?.status !== 403) toast.error(errMsg(e) || "Could not add user"); }
  };
  return (
    <div className="panel p-4 space-y-3" data-testid="admin-add-user">
      <div className="eyebrow flex items-center gap-1.5"><UserPlus className="w-3.5 h-3.5 text-sky-600" />Add user</div>
      <div className="flex flex-wrap gap-2">
        <input data-testid="admin-new-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Full name" className={`${inp} w-56`} />
        <select data-testid="admin-new-role" value={f.role_key} onChange={(e) => setF({ ...f, role_key: e.target.value, permissions: roles[e.target.value].permissions })} className={inp}>
          {Object.entries(roles).map(([k, r]) => <option key={k} value={k}>{r.role}</option>)}
        </select>
        <button data-testid="admin-new-submit" disabled={!f.name.trim()} onClick={save} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-40"><UserPlus className="w-3.5 h-3.5" />Add user</button>
      </div>
      <PermChecks id="new" permissions={permissions} value={f.permissions} onChange={(p) => setF({ ...f, permissions: p })} />
      <div className="text-[11px] text-slate-500">{roles[f.role_key].summary}</div>
    </div>
  );
}

function UserRow({ u, roles, permissions, self, onSaved }) {
  const [perms, setPerms] = useState(u.permissions);
  useEffect(() => setPerms(u.permissions), [u.permissions]);
  const upd = async (body, msg) => {
    try { await api.put(`/admin/users/${u.id}`, body); toast.success(msg); }
    catch (e) { if (e.response?.status !== 403) toast.error(errMsg(e) || "Update failed"); }
    onSaved();
  };
  const dirty = perms.join() !== u.permissions.join();
  return (
    <div className={`p-4 flex flex-wrap gap-4 items-start ${u.active ? "" : "opacity-60"}`} data-testid={`admin-user-${u.id}`}>
      <div className="w-52">
        <div className="flex items-center gap-2"><span className="w-7 h-7 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center text-[10px] font-semibold">{u.initials}</span>
          <div><div className="text-sm text-slate-900">{u.name}{self && <span className="ml-1 text-[10px] text-sky-700">(you)</span>}</div><div className="text-[10px] num text-slate-400">{u.id}</div></div></div>
        <div className={`text-[10px] mt-1 ${u.active ? "text-emerald-700" : "text-red-700"}`} data-testid={`admin-user-status-${u.id}`}>{u.active ? "Active" : "Deactivated"}{u.custom && <span className="text-amber-700"> · custom access</span>}</div>
      </div>
      <div className="flex-1 min-w-[280px] space-y-2">
        <select data-testid={`admin-role-${u.id}`} disabled={self} title={self ? "You cannot change your own access" : undefined} value={u.role_key} onChange={(e) => upd({ role_key: e.target.value }, `${u.name} → ${roles[e.target.value].role}`)} className={inp}>
          {Object.entries(roles).map(([k, r]) => <option key={k} value={k}>{r.role}</option>)}
        </select>
        <PermChecks id={u.id} permissions={permissions} value={perms} onChange={setPerms} disabled={self} />
      </div>
      <div className="flex flex-col gap-1.5 w-36">
        <button data-testid={`admin-save-${u.id}`} disabled={!dirty} onClick={() => upd({ permissions: perms }, `${u.name}: access updated`)} className="text-[11px] px-2 py-1 rounded bg-sky-600 text-white disabled:opacity-30">Save access</button>
        <button data-testid={`admin-active-${u.id}`} disabled={self} onClick={() => upd({ active: !u.active }, `${u.name} ${u.active ? "deactivated" : "reactivated"}`)} className={`text-[11px] px-2 py-1 rounded border disabled:opacity-30 ${u.active ? "border-red-500/40 text-red-700" : "border-emerald-500/40 text-emerald-700"}`}>{u.active ? "Deactivate" : "Reactivate"}</button>
      </div>
    </div>
  );
}

export default function Admin() {
  const { personaId, refreshPersonas, personaLabel } = useApp();
  const [d, setD] = useState(null);
  const [denied, setDenied] = useState(false);
  const load = useCallback(() => api.get("/admin/users").then((r) => { setD(r.data); setDenied(false); }).catch((e) => e.response?.status === 403 && setDenied(true)), []);
  useEffect(() => { load(); }, [load, personaId]);
  const saved = async () => { await load(); await refreshPersonas(); };
  return (
    <div className="flex-1 min-w-0 overflow-y-auto thin-scroll" data-testid="admin-page">
      <div className="px-6 py-4 border-b border-slate-200 flex flex-wrap items-end justify-between gap-3">
        <div><div className="eyebrow">Role-based access control</div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2"><Users className="w-5 h-5 text-sky-600" />User administration</h1></div>
        <AccessReportButton />
      </div>
      {denied ? (
        <div className="m-6 panel p-8 flex items-start gap-3 max-w-2xl" data-testid="admin-denied"><Lock className="w-5 h-5 text-red-600 mt-0.5" />
          <div><div className="text-sm text-slate-900">Restricted to System Administrators</div><div className="text-xs text-slate-500 mt-1">Acting as {personaLabel}. Switch to the Admin persona to add users and assign roles. This attempt was written to the access audit.</div></div></div>
      ) : !d ? <div className="p-8 text-sm text-slate-500">Loading…</div> : (
        <div className="p-6 space-y-4 max-w-[1400px]">
          <AddUser roles={d.roles} permissions={d.permissions} onSaved={saved} />
          <div className="panel" data-testid="admin-user-list">
            <div className="px-4 py-3 border-b border-slate-200 flex justify-between"><span className="eyebrow">Users & entitlements ({d.users.length})</span><span className="text-[11px] text-slate-500">Changes are audit-logged. Administrators cannot change their own access.</span></div>
            <div className="divide-y divide-slate-200">{d.users.map((u) => <UserRow key={u.id} u={u} roles={d.roles} permissions={d.permissions} self={u.id === personaId} onSaved={saved} />)}</div>
          </div>
        </div>
      )}
    </div>
  );
}
